import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { useCustomer } from '../customer-context.js';
import { formatOwners } from '../lib/owners.js';
import {
  Field,
  Row,
  Section,
  accountHoldsText,
  formatCurrency,
  formatDate,
  statusLabel,
  text,
  useAccountDetail,
  useCifAddress,
} from './account-detail-shared.js';

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  CURRENT: 'Current Account',
  DEPOSIT: 'Deposit Account',
  LOAN: 'Loan Account',
};

/**
 * SCRN 45 — Account Information Overview, current/savings (CCF-111).
 *
 * Faithful port of the customer's Figma "account info (current/savings)"
 * design: a read-only horizontal header box plus the four additional-
 * information sections (Statement details / Overdraft + DIRT / Charges /
 * Customer remarks + Compliance). The shared agent banner lives in the app
 * shell, so this screen renders only the `<main>` content.
 *
 * The account number arrives as the `?account=` query param from the Dashboard
 * tile. Detail is fetched from the Teller-ID-gated BFF
 * (`POST /api/accounts/details`).
 */
export function AccountInformationScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountNumber = searchParams.get('account') ?? '';
  const { detail, loading, error } = useAccountDetail(accountNumber);

  const typeLabel = detail
    ? (ACCOUNT_TYPE_LABEL[detail.AccountType ?? ''] ?? detail.AccountType)
    : '';
  const cur = detail?.Currency;

  // Account holder name(s), CCF-80 (issue #414). A joint account lists ALL
  // owners (comma-separated), sourced from the `Owners` field the BFF overlays
  // from customer/accounts/list — the same source the Dashboard tile (#11) and
  // Historic Transactions (#382) use. Falls back to the single verified banner
  // customer name when the account carries no owner data.
  const owners = formatOwners(detail?.Owners as string | string[] | undefined);
  const ownerValue = owners || customer.name || '—';

  // CCF-111 "Address" field (issue #603): show the customer's **CIF address**
  // (the customer master address), NOT the per-account Postal Address. Sourced
  // via the shared `useCifAddress` hook (issue #638 extended this same sourcing
  // to the Deposit + Term Loan overviews, so every account-information page shows
  // the CIF address consistently). Non-fatal: a lookup miss renders '—'.
  const cifAddress = useCifAddress();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route and forwards the in-scope account context.
  const onProcessSelect = makeProcessMenuHandler(navigate, accountNumber);

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="account-info-heading"
    >
      <div className="mb-4">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="account-info-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0"
        >
          Account information
        </h1>
        {/* Process-menu navigation (CCF-111 / Figma): jump to a related
            process. Pure navigation chooser — each route stays independently
            RBAC-gated (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {loading && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
        >
          Loading account…
        </p>
      )}

      {detail && (
        <>
          {/* Horizontal header box (CCF-111). */}
          <div className="border-2 border-[#dad7d3] p-6 mb-6 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 xl:divide-x xl:divide-[#e5e7eb]">
              <div className="xl:pr-6 flex flex-col gap-4">
                <Field label="IBAN" value={text(detail.Iban)} />
                <Field label="Account holder(s)" value={ownerValue} />
                <Field label="Address" value={text(cifAddress)} />
              </div>
              <div className="xl:px-6 flex flex-col gap-4">
                <Field
                  label="Current balance"
                  value={formatCurrency(detail.Balance, cur)}
                />
                <Field
                  label="Cheque / Mgmt hold"
                  value={formatCurrency(detail.ChequeMgmtHold, cur)}
                />
                <Field
                  label="Available balance"
                  value={formatCurrency(detail.AvailableBalance, cur)}
                />
              </div>
              <div className="xl:px-6 flex flex-col gap-4">
                <Field label="Account type" value={typeLabel} />
                <Field
                  label="Overdraft limit"
                  value={formatCurrency(detail.OverdraftLimit, cur)}
                />
                <Field label="Account holds" value={accountHoldsText(detail)} />
              </div>
              <div className="xl:pl-6 flex flex-col gap-4">
                <Field
                  label="Account status"
                  value={statusLabel(detail.Status)}
                />
                <Field
                  label="Account opened"
                  value={formatDate(detail.OpenedDate)}
                />
                <Field
                  label="Account closed"
                  value={formatDate(detail.ClosedDate)}
                />
              </div>
            </div>
          </div>

          {/* Additional account information — four vertical sections (CCF-111). */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <Section title="Statement details">
              <Row
                label="Last trans date:"
                value={formatDate(detail.LastTransactionDate)}
              />
              <Row
                label="Last cust date:"
                value={formatDate(detail.LastCustDate)}
              />
              <Row
                label="Send correspondence:"
                value={text(detail.SendCorrespondence)}
              />
              <Row
                label="Last statement date:"
                value={formatDate(detail.LastStatementDate)}
              />
              <Row
                label="Statement frequency:"
                value={text(detail.StatementFrequency)}
              />
              <Row
                label="Statement cycle:"
                value={text(detail.StatementCycle)}
              />
              <Row
                label="Next statement date:"
                value={formatDate(detail.NextStatementDate)}
              />
              <Row
                label="Last date in credit:"
                value={formatDate(detail.LastDateInCredit)}
              />
            </Section>

            <Section title="Overdraft">
              <Row
                label="Overdraft limit:"
                value={formatCurrency(detail.OverdraftLimit, cur)}
              />
              <Row
                label="Overdraft interest rate:"
                value={text(detail.OverdraftInterestRate)}
              />
              <Row
                label="Overdraft plus rate:"
                value={text(detail.OverdraftPlusRate)}
              />
              <Row
                label="Credit interest due:"
                value={formatCurrency(detail.CreditInterestDue, cur)}
              />
              <Row
                label="Next credit interest date:"
                value={formatDate(detail.NextCreditInterestDate)}
              />
              <Row
                label="Credit base rate:"
                value={text(detail.CreditBaseRate)}
              />
              <Row
                label="Total overdraft interest:"
                value={formatCurrency(detail.TotalOverdraftInterest, cur)}
              />
              <div className="border-t border-[#e5e7eb] pt-4 mt-2">
                <h3 className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4 m-0">
                  DIRT
                </h3>
                <div className="flex flex-col gap-4">
                  <Row
                    label="DIRT tax code:"
                    value={text(detail.DirtTaxCode)}
                  />
                  <Row
                    label="DIRT flag ind:"
                    value={text(detail.DirtFlagIndicator)}
                  />
                  <Row
                    label="DIRT tax paid YTD:"
                    value={formatCurrency(detail.DirtTaxPaidYtd, cur)}
                  />
                  <Row
                    label="Interest set off:"
                    value={text(detail.InterestSetOff)}
                  />
                  <Row
                    label="Interest set off IBAN:"
                    value={text(detail.InterestSetOffIban)}
                  />
                  <Row
                    label="Interest set off BIC:"
                    value={text(detail.InterestSetOffBic)}
                  />
                </div>
              </div>
            </Section>

            <Section title="Charges">
              <Row
                label="Exempt Od/penalty fee:"
                value={text(detail.ExemptOdPenaltyFee)}
              />
              <Row
                label="Maximum Od balance:"
                value={formatCurrency(detail.MaxOverdraftBalance, cur)}
              />
              <Row
                label="Days overdrawn since:"
                value={text(detail.DaysOverdrawnSince)}
              />
              <Row
                label="Date last deposit:"
                value={formatDate(detail.DateLastDeposit)}
              />
              <Row
                label="Exempt bank fees:"
                value={text(detail.ExemptBankFees)}
              />
              <Row
                label="Account remarks:"
                value={text(detail.AccountRemarks)}
              />
              <Row
                label="Interest indicator:"
                value={text(detail.InterestIndicator)}
              />
              <Row
                label="Set off indicator:"
                value={text(detail.SetOffIndicator)}
              />
              <Row
                label="Od set off account:"
                value={text(detail.OdSetOffAccount)}
              />
              <Row label="Home base:" value={text(detail.HomeBase)} />
              <Row
                label="Reference rate group:"
                value={text(detail.ReferenceRateGroup)}
              />
              <Row
                label="Review frequency:"
                value={text(detail.ReviewFrequency)}
              />
              <Row label="Review date:" value={formatDate(detail.ReviewDate)} />
              <Row label="Expiry date:" value={formatDate(detail.ExpiryDate)} />
              <Row label="Purpose code:" value={text(detail.PurposeCode)} />
            </Section>

            <div className="flex flex-col gap-6">
              <Section title="Customer remarks / Holds">
                <Row
                  label="Customer remarks:"
                  value={text(detail.CustomerRemarksIndicator)}
                />
                <Row
                  label="Customer hold:"
                  value={text(detail.CustomerHoldIndicator)}
                />
              </Section>
              <Section title="Compliance information">
                <Row
                  label="Reason for opening:"
                  value={text(detail.ReasonForOpening)}
                />
                <Row
                  label="Source of funds:"
                  value={text(detail.SourceOfFunds)}
                />
                <Row label="Risk code:" value={text(detail.RiskCode)} />
              </Section>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
